import React, { useState, useEffect, useRef } from 'react';
import { api, videoService } from '../../services/api';
import { PlayIcon, PauseIcon, Volume2Icon, VolumeXIcon, MaximizeIcon, MinimizeIcon } from 'lucide-react';


interface ConfigureServiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  service: {
    id: number;
    name: string;
    status?: string;
    phone_id?: string;
    meta_api_key?: string;
    whmcs_service_id?: number;
    whmcs_order_id?: number;
    plan?: {
      name: string;
    };
  } | null;
  onConfigured: () => void;
}

interface VideoPlayerProps {
  src: string;
  className?: string;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({ src, className = '' }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [controlsTimeout, setControlsTimeout] = useState<NodeJS.Timeout | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [isLoadingVideo, setIsLoadingVideo] = useState(true);
  const [animationFrame, setAnimationFrame] = useState<number | null>(null);
  const [isBuffering, setIsBuffering] = useState(false);

  // Load protected video on component mount
  useEffect(() => {
    const loadProtectedVideo = async () => {
      try {
        setIsLoadingVideo(true);
        setHasError(false);

        // Extract filename from src (e.g., "/wabby-tutorial.mp4" -> "wabby-tutorial.mp4")
        const filename = src.split('/').pop();
        if (!filename) {
          throw new Error('Invalid video filename');
        }


        // Try to load protected video first
        try {
          const response = await videoService.getProtectedVideo(filename);

          // Create blob URL for the video
          const blob = new Blob([response.data], { type: 'video/mp4' });
          const url = URL.createObjectURL(blob);
          setVideoUrl(url);

          // Small delay to ensure video element processes the new URL
          setTimeout(() => {
            setIsLoadingVideo(false);
          }, 100);
        } catch (protectedVideoError) {
          // Protected video failed, trying fallback

          // Fallback: try to load from public directory
          try {
            const fallbackUrl = `${process.env.PUBLIC_URL || ''}${src}`;

            const response = await fetch(fallbackUrl);

            if (response.ok) {
              const blob = new Blob([await response.blob()], { type: 'video/mp4' });
              const url = URL.createObjectURL(blob);
              setVideoUrl(url);
            } else {
              throw new Error(`Video not found in public directory: ${response.status} ${response.statusText}`);
            }
          } catch (fallbackError) {
            throw fallbackError;
          }
        }

      } catch (error) {
        setHasError(true);
      } finally {
        setIsLoadingVideo(false);
      }
    };

    loadProtectedVideo();

    // Cleanup blob URL on unmount
    return () => {
      if (videoUrl) {
        URL.revokeObjectURL(videoUrl);
      }
    };
  }, [src]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const updateTime = () => {
      setCurrentTime(video.currentTime);

      // Continue updating if video is playing
      if (!video.paused && !video.ended) {
        const frame = requestAnimationFrame(updateTime);
        setAnimationFrame(frame);
      } else {
        if (animationFrame) {
          cancelAnimationFrame(animationFrame);
          setAnimationFrame(null);
        }
      }
    };

    const updateDuration = () => {
      setDuration(video.duration);
    };

    const handlePlay = () => {
      setIsPlaying(true);
      setShowControls(true); // Show controls when playing
    };

    const handlePause = () => {
      setIsPlaying(false);
      setShowControls(true); // Always show controls when paused
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setShowControls(true);
    };

    const handleWaiting = () => {
      setIsBuffering(true);
    };

    const handleCanPlay = () => {
      setIsBuffering(false);
      setHasError(false);
    };

    const handleLoadedData = () => {
      setIsLoadingVideo(false);
    };

    const handleError = (e: Event) => {
      const video = e.target as HTMLVideoElement;

      // Only set error if it's a critical error and we don't have a valid video URL
      if (!videoUrl && video.error) {
        setHasError(true);
      }

      setIsLoadingVideo(false);
      setIsBuffering(false);
    };

    // Add all event listeners
    video.addEventListener('timeupdate', updateTime);
    video.addEventListener('loadedmetadata', updateDuration);
    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('waiting', handleWaiting);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('error', handleError);

    // Force initial state update
    if (video.readyState >= 1) {
      updateDuration();
    }

    return () => {
      video.removeEventListener('timeupdate', updateTime);
      video.removeEventListener('loadedmetadata', updateDuration);
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('waiting', handleWaiting);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('error', handleError);

      // Cancel any ongoing animation frame
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [videoUrl]); // Re-attach when video URL changes

  // Reset video state when URL changes
  useEffect(() => {
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);
    setShowControls(true);
    setIsBuffering(false);
  }, [videoUrl]);

  // Auto-hide controls when playing (with delay)
  useEffect(() => {
    if (isPlaying && showControls) {
      if (controlsTimeout) {
        clearTimeout(controlsTimeout);
      }
      const timeout = setTimeout(() => {
        setShowControls(false);
      }, 3000); // Hide after 3 seconds of playing
      setControlsTimeout(timeout);
    }

    return () => {
      if (controlsTimeout) {
        clearTimeout(controlsTimeout);
      }
    };
  }, [isPlaying, showControls]);

  const togglePlay = async () => {
    const video = videoRef.current;
    if (!video) return;

    try {
      if (isPlaying) {
        video.pause();
        setIsPlaying(false);
      } else {
        await video.play();
        setIsPlaying(true);
      }
      setShowControls(true); // Always show controls when user interacts

      // Force a re-render to update button state immediately
      setTimeout(() => {
        if (video) {
          setIsPlaying(!video.paused);
        }
      }, 10);
    } catch (error) {
      // Silently handle play/pause errors
    }
  };

  const handleMouseMove = () => {
    if (isPlaying) {
      setShowControls(true);
      // Auto-hide after 3 seconds if still playing
      if (controlsTimeout) {
        clearTimeout(controlsTimeout);
      }
      const timeout = setTimeout(() => {
        setShowControls(false);
      }, 3000);
      setControlsTimeout(timeout);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    if (!video || !duration) return;

    const newTime = (parseFloat(e.target.value) / 100) * duration;
    video.currentTime = newTime;
    setCurrentTime(newTime);

    // Ensure controls stay visible when seeking
    setShowControls(true);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    if (!video) return;

    const newVolume = parseFloat(e.target.value) / 100;
    video.volume = newVolume;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isMuted) {
      video.volume = volume;
      setIsMuted(false);
    } else {
      video.volume = 0;
      setIsMuted(true);
    }
  };

  const toggleFullscreen = () => {
    const video = videoRef.current;
    if (!video) return;

    if (!isFullscreen) {
      if (video.requestFullscreen) {
        video.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
    setIsFullscreen(!isFullscreen);
  };

  const openInNewWindow = async () => {
    try {
      // Try to use protected video URL first
      if (videoUrl) {
        window.open(videoUrl, '_blank');
      } else {
        // Fallback to original src if protected video failed to load
        window.open(src, '_blank', 'width=800,height=600');
      }
    } catch (error) {
      // Fallback to original src if there's an error
      window.open(src, '_blank', 'width=800,height=600');
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };


  // Only show error if we have a critical error AND no video URL
  if (hasError && !videoUrl) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 rounded-lg ${className}`}>
        <div className="text-center p-8">
          <div className="text-gray-400 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </div>
          <p className="text-gray-600 mb-4">Vídeo tutorial não encontrado</p>
          <button
            onClick={() => window.open(src, '_blank', 'width=800,height=600')}
            className="text-cyan-600 hover:text-cyan-800 underline"
          >
            Tentar abrir em nova janela
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`relative bg-black rounded-lg overflow-hidden ${className}`}
      onMouseMove={handleMouseMove}
    >
      {/* Loading/Buffering indicator */}
      {(isLoadingVideo || isBuffering) && (
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 z-10">
          <div className="text-white text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600 mx-auto mb-2"></div>
            <p className="text-sm">{isLoadingVideo ? 'Carregando vídeo...' : 'Carregando...'}</p>
          </div>
        </div>
      )}

      {/* Only render video when we have a valid URL */}
      {(videoUrl || src) && (
        <video
        key={videoUrl || src} // Force re-render when video source changes
        ref={videoRef}
        src={videoUrl || src}
        className="w-full h-full object-contain"
        controls={false}
        disablePictureInPicture
        controlsList="nodownload"
        onMouseEnter={() => setShowControls(true)}
        onMouseLeave={(e) => {
          // Only hide controls if mouse is not on the controls container
          const controlsContainer = e.currentTarget.parentElement?.querySelector('.controls-container');

          // Use setTimeout to give user time to move to controls
          setTimeout(() => {
            if (controlsContainer && !controlsContainer.matches(':hover')) {
              setShowControls(false);
            }
          }, 100); // Small delay to allow mouse movement to controls
        }}
        onContextMenu={(e) => e.preventDefault()} // Prevent right-click menu
        />
      )}

      {/* Video Controls */}
      <div
        className={`controls-container absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 transition-opacity duration-300 ${
          showControls ? 'opacity-100' : 'opacity-0'
        }`}
        onMouseEnter={() => setShowControls(true)}
        onMouseLeave={(e) => {
          // Only hide controls if mouse is not on the video element and not on controls
          const video = videoRef.current;
          const controlsContainer = e.currentTarget.parentElement?.querySelector('.controls-container');

          // Use setTimeout to give user time to move to controls
          setTimeout(() => {
            if (video && !video.matches(':hover') && controlsContainer && !controlsContainer.matches(':hover')) {
              setShowControls(false);
            }
          }, 100); // Small delay to allow mouse movement to controls
        }}
      >
        {/* Progress Bar */}
        <div className="mb-3">
          <input
            type="range"
            min="0"
            max="100"
            value={duration > 0 ? (currentTime / duration) * 100 : 0}
            onChange={handleSeek}
            className="w-full h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer slider"
            step="0.1"
          />
        </div>

        {/* Control Buttons */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {/* Play/Pause */}
            <button
              onClick={togglePlay}
              className="text-white hover:text-cyan-400 transition-colors"
            >
              {isPlaying ? <PauseIcon className="h-5 w-5" /> : <PlayIcon className="h-5 w-5" />}
            </button>

            {/* Time Display */}
            <span className="text-white text-sm">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>

            {/* Volume Controls */}
            <div className="flex items-center space-x-2">
              <button
                onClick={toggleMute}
                className="text-white hover:text-cyan-400 transition-colors"
              >
                {isMuted || volume === 0 ? <VolumeXIcon className="h-4 w-4" /> : <Volume2Icon className="h-4 w-4" />}
              </button>
              <input
                type="range"
                min="0"
                max="100"
                value={isMuted ? 0 : volume * 100}
                onChange={handleVolumeChange}
                className="w-20 h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer slider"
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {/* Open in New Window */}
            <button
              onClick={openInNewWindow}
              className="text-white hover:text-cyan-400 transition-colors"
              title="Abrir em nova janela"
            >
              <MaximizeIcon className="h-4 w-4" />
            </button>

            {/* Fullscreen */}
            <button
              onClick={toggleFullscreen}
              className="text-white hover:text-cyan-400 transition-colors"
            >
              {isFullscreen ? <MinimizeIcon className="h-4 w-4" /> : <MaximizeIcon className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ConfigureServiceModal: React.FC<ConfigureServiceModalProps> = ({
  isOpen,
  onClose,
  service,
  onConfigured
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showVideo, setShowVideo] = useState(true);
  const [config, setConfig] = useState({
    meta_api_token: '',
    meta_phone_id: ''
  });

  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens
      setConfig({
        meta_api_token: '',
        meta_phone_id: ''
      });
    }
  }, [isOpen, service]);


  const handleConfigure = async () => {
    // Validar campos obrigatórios - permitir configuração mesmo sem whmcs_order_id (backend consegue lidar)
    if (!config.meta_api_token.trim() || !config.meta_phone_id.trim()) {
      import('react-hot-toast').then(({ default: toast }) => {
        toast.error('Todos os campos são obrigatórios para a instalação do serviço');
      });
      return;
    }

    // Verificar se temos pelo menos um ID do WHMCS para identificar o serviço
    if (!service?.whmcs_order_id && !service?.whmcs_service_id) {
      import('react-hot-toast').then(({ default: toast }) => {
        toast.error('Serviço não possui identificação válida no sistema de cobrança');
      });
      return;
    }

    try {
      setIsLoading(true);

      // Usar service.id diretamente para evitar conflitos com campos WHMCS
      const serviceId = service.id;

      console.log('🔍 [FRONTEND] Enviando configuração para backend:', {
        serviceId,
        serviceName: service.name,
        payload: {
          meta_api_token: config.meta_api_token.trim().substring(0, 10) + '...', // Log parcial por segurança
          meta_phone_id: config.meta_phone_id.trim()
        }
      });

      const response = await api.post(`/services/${serviceId}/configure`, {
        meta_api_token: config.meta_api_token.trim(),
        meta_phone_id: config.meta_phone_id.trim()
      });

      // Verificar se a configuração foi bem-sucedida
      if (response.data.success) {
        // Sucesso completo (Meta API + Provisionamento)
        import('react-hot-toast').then(({ default: toast }) => {
          toast.success('Serviço configurado com sucesso! 🎉');
        });
      } else {
        // Falha na configuração (Meta API ou Provisionamento)
        console.log('❌ [FRONTEND] Configuração falhou:', response.data.message);

        // Mostrar erro no toast e fechar modal
        import('react-hot-toast').then(({ default: toast }) => {
          toast.error(response.data.message || 'Erro ao configurar serviço');
        });

        // Fechar modal para permitir nova tentativa
        onClose();
        return;
      }


      // Só prosseguir se foi sucesso completo
      if (response.data.success) {
        // Pequeno delay para garantir que o backend tenha tempo de processar
        setTimeout(() => {
          onConfigured();
          onClose();
        }, 500);
      }
      // Se não foi sucesso, manter modal aberto para correção
    } catch (err: any) {
      const errorData = err.response?.data;

      // Se é erro de pagamento pendente, mostrar toast em vez de erro no modal
      if (errorData?.requires_payment) {
        // Usar toast notification para erro de pagamento
        import('react-hot-toast').then(({ default: toast }) => {
          toast.error(errorData.message || 'Aguardando pagamento da fatura para configurar o serviço');
        });
        onClose();
        return;
      }

      // Para outros erros, mostrar no toast e fechar modal
      const errorMessage = errorData?.message || 'Erro ao configurar serviço';
      console.error('❌ [FRONTEND] Erro na configuração:', errorData);

      import('react-hot-toast').then(({ default: toast }) => {
        toast.error(errorMessage);
      });

      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  // Add CSS styles for video player
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      /* Custom video player styles */
      .slider::-webkit-slider-thumb {
        appearance: none;
        height: 12px;
        width: 12px;
        border-radius: 50%;
        background: #06b6d4;
        cursor: pointer;
        border: 2px solid #ffffff;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
      }

      .slider::-webkit-slider-track {
        width: 100%;
        height: 4px;
        cursor: pointer;
        background: rgba(255, 255, 255, 0.3);
        border-radius: 2px;
      }

      .slider::-moz-range-thumb {
        height: 12px;
        width: 12px;
        border-radius: 50%;
        background: #06b6d4;
        cursor: pointer;
        border: 2px solid #ffffff;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
      }

      .slider::-moz-range-track {
        width: 100%;
        height: 4px;
        cursor: pointer;
        background: rgba(255, 255, 255, 0.3);
        border-radius: 2px;
      }

      /* Prevent video context menu and downloads */
      video::-webkit-media-controls {
        display: none !important;
      }

      video::-webkit-media-controls-panel {
        display: none !important;
      }

      video {
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  if (!isOpen || !service) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-60" onClick={onClose}>
      <div className="h-full overflow-y-auto flex items-center justify-center p-4">
        <div
          className="w-full max-w-6xl bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-2xl border border-gray-200"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
               <div>
                 <h3 className="text-2xl font-bold text-gray-900 mb-2">
                   Configurar Serviço
                 </h3>
                 <p className="text-gray-600">
                   Configure as opções técnicas do seu serviço <strong>{service.name}</strong>
                 </p>
                 {service.plan && (
                   <p className="text-sm text-gray-500 mt-1">
                     Plano: {service.plan.name}
                   </p>
                 )}
               </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-colors"
              >
                ✕
              </button>
            </div>

            {/* Video Tutorial and Configuration */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Video Tutorial */}
              <div className="mb-8 lg:col-span-1">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-gray-800">Tutorial em Vídeo</h4>
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => setShowVideo(!showVideo)}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-lg shadow-sm text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 transition-all duration-200"
                    >
                      {showVideo ? '👁️ Ocultar' : '👁️ Mostrar'}
                    </button>
                    <button
                      type="button"
                      onClick={() => window.open('/wabby-tutorial.mp4', '_blank', 'width=800,height=600')}
                      className="inline-flex items-center px-3 py-1.5 border border-cyan-300 text-xs font-medium rounded-lg shadow-sm text-cyan-700 bg-cyan-50 hover:bg-cyan-100 hover:border-cyan-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 transition-all duration-200"
                    >
                      🔗 Nova janela
                    </button>
                  </div>
                </div>
                {showVideo && (
                  <VideoPlayer
                    src="/wabby-tutorial.mp4"
                    className="w-full h-56 md:h-64"
                  />
                )}
              </div>

              {/* WhatsApp Configuration */}
              <div className="max-w-2xl lg:col-span-1">
                <div className="p-8 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
                   <div className="flex items-center justify-between mb-6">
                     <div className="flex items-center">
                       <div className="bg-green-100 rounded-full p-3 mr-4">
                         <span className="text-green-600 text-xl">📱</span>
                       </div>
                       <div>
                         <h4 className="text-xl font-semibold text-gray-900">Integração WhatsApp Business</h4>
                         <p className="text-gray-600 text-sm">Configure a integração com o WhatsApp Business</p>
                       </div>
                     </div>
                   </div>

                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Phone ID do Meta Business <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        className="block w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all bg-white"
                        value={config.meta_phone_id}
                        onChange={(e) => setConfig({ ...config, meta_phone_id: e.target.value })}
                        placeholder="Ex: 123456789012345"
                      />
                      <p className="text-xs text-gray-600 mt-1">
                        ID do número de telefone configurado no Meta Business
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Token da Meta API <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="password"
                        required
                        className="block w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all bg-white"
                        value={config.meta_api_token}
                        onChange={(e) => setConfig({ ...config, meta_api_token: e.target.value })}
                        placeholder="Token de acesso da Meta API"
                      />
                      <p className="text-xs text-gray-600 mt-1">
                        Token permanente de acesso à API do WhatsApp Business
                      </p>
                    </div>
                  </div>

                  <div className="bg-green-100 rounded-lg p-4 mt-6">
                    <div className="flex items-start">
                      <span className="text-green-600 mr-2">💡</span>
                      <div className="text-xs text-green-800">
                        <p className="font-semibold mb-1">Como obter os dados da Meta API:</p>
                        <ol className="list-decimal list-inside space-y-1 ml-2">
                          <li>Acesse <a href="https://developers.facebook.com/apps" target="_blank" rel="noopener noreferrer" className="underline">Facebook Developers</a></li>
                          <li>Crie uma aplicação WhatsApp Business</li>
                          <li>Configure o número de telefone</li>
                          <li>Gere um token permanente</li>
                          <li>Copie o Phone ID e o Token</li>
                        </ol>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-4 pt-8 border-t border-gray-200 mt-8">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 border-2 border-gray-300 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 transition-all"
                disabled={isLoading}
              >
                Cancelar
              </button>
              <button
                onClick={handleConfigure}
                disabled={!config.meta_api_token.trim() || !config.meta_phone_id.trim() || isLoading}
                className="px-8 py-3 border border-transparent rounded-xl shadow-lg text-sm font-semibold text-white bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105"
              >
                {isLoading ? 'Instalando Serviço...' : 'Realizar configuração'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};